import { css, cssManager } from '@design.estate/dees-element';

export const wysiwygStyles = css`
  :host {
    display: block;
    position: relative;
  }

  .wysiwyg-container {
    background: var(--dees-color-bg-primary);
    border: 1px solid var(--dees-color-border-default);
    border-radius: var(--dees-radius-lg);
    min-height: 200px;
    padding: 24px;
    position: relative;
    transition: border-color var(--dees-transition-fast) var(--dees-ease-standard);
    color: var(--dees-color-text-primary);
  }

  .wysiwyg-container:hover {
    border-color: var(--dees-color-border-strong);
  }

  :host([disabled]) .wysiwyg-container { opacity: .5; }

  .wysiwyg-container:focus-within {
    border-color: var(--dees-color-accent-primary);
    outline: 2px solid var(--dees-color-focus-ring);
    outline-offset: 1px;
  }

  /* Visual hint for text selection */
  .editor-content:hover {
    cursor: text;
  }

  .editor-content {
    outline: none;
    min-height: 160px;
    margin: 0 -8px;
    padding: 0 8px;
  }

  .block {
    margin: 0;
    padding: 4px 0;
    position: relative;
    transition: all 0.15s ease;
    min-height: 1.6em;
    color: var(--dees-color-text-primary);
  }

  /* First and last blocks don't need extra spacing */
  .block-wrapper:first-child .block {
    margin-top: 0 !important;
  }

  .block-wrapper:last-child .block {
    margin-bottom: 0;
  }

  .block.selected {
    background: color-mix(in srgb, var(--dees-color-accent-primary) 5%, transparent);
    outline: 2px solid color-mix(in srgb, var(--dees-color-accent-primary) 20%, transparent);
    outline-offset: -2px;
    border-radius: 4px;
    margin-left: -8px;
    margin-right: -8px;
    padding-left: 8px;
    padding-right: 8px;
  }

  .block[contenteditable] {
    outline: none;
  }

  .block.paragraph {
    font-size: 16px;
    line-height: 1.6;
    font-weight: 400;
  }

  .block.paragraph:empty::before {
    content: "Type '/' for commands...";
    color: var(--dees-color-text-muted);
    pointer-events: none;
    font-size: 16px;
    line-height: 1.6;
    font-weight: 400;
  }

  .block.heading-1 {
    font-size: 32px;
    font-weight: 700;
    line-height: 1.2;
    color: var(--dees-color-text-primary);
  }

  .block.heading-1:empty::before {
    content: "Heading 1";
    color: var(--dees-color-text-muted);
    pointer-events: none;
    font-size: 32px;
    line-height: 1.2;
    font-weight: 700;
  }

  .block.heading-2 {
    font-size: 24px;
    font-weight: 600;
    line-height: 1.3;
    color: var(--dees-color-text-primary);
  }

  .block.heading-2:empty::before {
    content: "Heading 2";
    color: var(--dees-color-text-muted);
    pointer-events: none;
    font-size: 24px;
    line-height: 1.3;
    font-weight: 600;
  }

  .block.heading-3 {
    font-size: 20px;
    font-weight: 600;
    line-height: 1.4;
    color: var(--dees-color-text-primary);
  }

  .block.heading-3:empty::before {
    content: "Heading 3";
    color: var(--dees-color-text-muted);
    pointer-events: none;
    font-size: 20px;
    line-height: 1.4;
    font-weight: 600;
  }

  .block.quote {
    border-left: 2px solid var(--dees-color-border-default);
    padding-left: 20px;
    font-style: italic;
    color: var(--dees-color-text-secondary);
    margin-left: 0;
    margin-right: 0;
    line-height: 1.6;
  }

  .block.quote:empty::before {
    content: "Quote";
    color: var(--dees-color-text-muted);
    pointer-events: none;
    font-size: 16px;
    line-height: 1.6;
    font-weight: 400;
    font-style: italic;
  }

  .code-block-container {
    position: relative;
    margin: 20px 0;
  }

  .code-language {
    position: absolute;
    top: 0;
    right: 0;
    background: var(--dees-color-badge-default-bg);
    color: var(--dees-color-text-muted);
    padding: 4px 12px;
    font-size: 12px;
    border-radius: 0 4px 0 4px;
    font-family: system-ui, 'Segoe UI', Roboto, sans-serif;
    text-transform: lowercase;
    z-index: 1;
  }

  .block.code {
    background: var(--dees-color-code-block-bg);
    border: 1px solid var(--dees-color-border-default);
    border-radius: 4px;
    padding: 16px;
    padding-top: 32px; /* Make room for language indicator */
    font-family: 'Monaco', 'Inconsolata', 'Fira Code', monospace;
    font-size: 14px;
    line-height: 1.5;
    white-space: pre-wrap;
    color: var(--dees-color-text-primary);
    overflow-x: auto;
  }

  .block.code:empty::before {
    content: "// Code block";
    color: var(--dees-color-text-muted);
    pointer-events: none;
    font-family: 'Monaco', 'Inconsolata', 'Fira Code', monospace;
    font-size: 14px;
    line-height: 1.6;
    font-weight: 400;
  }

  .block.list {
    padding-left: 0;
  }

  .block.list ul,
  .block.list ol {
    margin: 0;
    padding: 0 0 0 24px;
    list-style-position: outside;
  }

  .block.list ul {
    list-style: disc;
  }

  .block.list ol {
    list-style: decimal;
  }

  .block.list li {
    margin-bottom: 8px;
    line-height: 1.6;
  }

  .block.list li:last-child {
    margin-bottom: 0;
  }

  .block.divider {
    text-align: center;
    padding: 20px 0;
    cursor: default;
    pointer-events: none;
  }

  .block.divider hr {
    border: none;
    border-top: 1px solid var(--dees-color-border-default);
    margin: 0;
  }

  .slash-menu {
    position: absolute;
    background: var(--dees-color-bg-primary);
    border: 1px solid var(--dees-color-border-default);
    border-radius: 4px;
    box-shadow: var(--dees-shadow-sm);
    padding: 4px;
    z-index: 1000;
    min-width: 220px;
    max-height: 300px;
    overflow-y: auto;
    pointer-events: auto;
    user-select: none;
  }

  .slash-menu-item {
    padding: 8px 10px;
    cursor: pointer;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;
    gap: 12px;
    border-radius: 3px;
    color: var(--dees-color-text-primary);
    font-size: 14px;
  }

  .slash-menu-item:hover,
  .slash-menu-item.selected {
    background: var(--dees-color-badge-default-bg);
    color: var(--dees-color-text-primary);
  }

  .slash-menu-item .icon {
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: var(--dees-color-text-muted);
    font-weight: 600;
  }

  .slash-menu-item:hover .icon,
  .slash-menu-item.selected .icon {
    color: var(--dees-color-accent-primary);
  }

  .toolbar {
    position: absolute;
    top: -40px;
    left: 0;
    background: var(--dees-color-bg-primary);
    border: 1px solid var(--dees-color-border-default);
    border-radius: 4px;
    box-shadow: var(--dees-shadow-sm);
    padding: 4px;
    display: none;
    gap: 4px;
    z-index: 1000;
  }

  .toolbar.visible {
    display: flex;
  }

  .toolbar-button {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 3px;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--dees-color-text-primary);
  }

  .toolbar-button:hover {
    background: var(--dees-color-badge-default-bg);
    color: var(--dees-color-accent-primary);
  }

  /* Drag and Drop Styles */
  .block-wrapper {
    position: relative;
    transition: transform 0.3s ease, opacity 0.2s ease;
  }

  /* Ensure proper spacing context for blocks */
  .block-wrapper + .block-wrapper .block {
    margin-top: 16px;
  }

  /* Override for headings following other blocks */
  .block-wrapper + .block-wrapper .block.heading-1,
  .block-wrapper + .block-wrapper .block.heading-2,
  .block-wrapper + .block-wrapper .block.heading-3 {
    margin-top: 24px;
  }

  /* Code and quote blocks need consistent spacing */
  .block-wrapper + .block-wrapper .block.code,
  .block-wrapper + .block-wrapper .block.quote {
    margin-top: 20px;
  }

  .drag-handle {
    position: absolute;
    left: -28px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    cursor: grab;
    opacity: 0;
    transition: opacity 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--dees-color-text-muted);
    border-radius: 4px;
  }

  .drag-handle::before {
    content: "⋮⋮";
    font-size: 12px;
    letter-spacing: -2px;
  }

  .block-wrapper:hover .drag-handle {
    opacity: 1;
  }

  .drag-handle:hover {
    color: var(--dees-color-text-muted);
    background: var(--dees-color-badge-default-bg);
  }

  .drag-handle:active {
    cursor: grabbing;
    background: var(--dees-color-border-default);
  }

  .block-wrapper.dragging {
    opacity: 0.8;
    pointer-events: none;
    position: relative;
    z-index: 2001;
    transition: none !important;
  }


  /* Blocks that should move out of the way */
  .block-wrapper.move-down {
    transform: translateY(var(--drag-offset, 0px));
  }

  .block-wrapper.move-up {
    transform: translateY(calc(-1 * var(--drag-offset, 0px)));
  }

  /* Drop indicator */
  .drop-indicator {
    position: absolute;
    left: 0;
    right: 0;
    background: color-mix(in srgb, var(--dees-color-accent-primary) 5%, transparent);
    border: 2px dashed var(--dees-color-accent-primary);
    border-radius: 4px;
    transition: top 0.2s ease, height 0.2s ease;
    pointer-events: none;
    z-index: 1999;
    box-sizing: border-box;
  }

  /* Remove old drag-over styles */
  .block-wrapper.drag-over-before,
  .block-wrapper.drag-over-after {
    /* No longer needed, using drop indicator instead */
  }

  .editor-content.dragging * {
    user-select: none;
  }

  /* Block Settings Button - Removed in favor of context menu */

  /* Text Selection Styles */
  .block ::selection {
    background: color-mix(in srgb, var(--dees-color-accent-primary) 20%, transparent);
    color: inherit;
  }

  /* Formatting Menu */
  .formatting-menu {
    position: absolute;
    background: var(--dees-color-bg-primary);
    border: 1px solid var(--dees-color-border-default);
    border-radius: 4px;
    box-shadow: var(--dees-shadow-sm);
    padding: 4px;
    display: flex;
    gap: 2px;
    z-index: 1001;
    animation: fadeInScale 0.15s ease-out;
  }

  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.98) translateY(2px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  .format-button {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 3px;
    transition: all 0.15s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--dees-color-text-primary);
    font-weight: 600;
    font-size: 14px;
    position: relative;
  }

  .format-button:hover {
    background: var(--dees-color-badge-default-bg);
    color: var(--dees-color-accent-primary);
  }

  .format-button:active {
    transform: scale(0.95);
  }

  .format-button.bold {
    font-weight: 700;
  }

  .format-button.italic {
    font-style: italic;
  }

  .format-button.underline {
    text-decoration: underline;
  }

  .format-button .code-icon {
    font-family: 'Monaco', 'Inconsolata', 'Fira Code', monospace;
    font-size: 12px;
  }

  /* Applied format styles in content */
  .block strong,
  .block b {
    font-weight: 600;
    color: var(--dees-color-text-primary);
  }

  .block em,
  .block i {
    font-style: italic;
  }

  .block u {
    text-decoration: underline;
  }

  .block strike,
  .block s {
    text-decoration: line-through;
    opacity: 0.7;
  }

  .block code {
    background: var(--dees-color-badge-default-bg);
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Monaco', 'Inconsolata', 'Fira Code', monospace;
    font-size: 0.9em;
    color: var(--dees-color-text-primary);
  }

  .block a {
    color: var(--dees-color-accent-primary);
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: border-color 0.15s ease;
  }

  .block a:hover {
    border-bottom-color: var(--dees-color-accent-primary);
  }
`;
